import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Button,
  ButtonBase,
  Card,
  CardContent,
  Collapse,
  Fade,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

import {
  adminUpdateLessonHistoryTutorPenalty,
  ILessonHistory,
} from '../../../../../../../../../../store/V2/LessonHistorySlice';
import { TPTextField } from '../../../../../../../../../../components/Inputs';
import { useAppDispatch } from '../../../../../../../../../../hooks/Store';

interface TutorPenaltyTabProps {
  lessonHistory: ILessonHistory;
  refreshListEnabled?: boolean;
  refreshList?: () => void;
}

type TState = {
  customPenalty: string;
  customPenaltyReason: string;
};

type TPenaltyItem = {
  penalty: number;
  title: string;
  penaltyReason: string;
};

const penaltyCardContainerStyles: SxProps = {
  backgroundColor: '#F1F5F9',
};

const penaltyData: TPenaltyItem[] = [
  {
    penalty: 25,
    title: 'Late Launching (upto 10 minutes): 25%',
    penaltyReason: 'Late Launching (upto 10 minutes)',
  },
  {
    penalty: 50,
    title: 'Late Launching - goes beyond 10 minutes: 50%',
    penaltyReason: 'Late Launching - goes beyond 10 minutes',
  },
  {
    penalty: 200,
    title: 'No show : 200%',
    penaltyReason: 'No show',
  },
  {
    penalty: 400,
    title: 'No show : 400%',
    penaltyReason: 'No show',
  },
  {
    penalty: 40,
    title: 'No feedback form : 40%',
    penaltyReason: 'No feedback form',
  },
];

const CustomContainer = ({ children }) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>{children}</CardContent>
    </Card>
  );
};

const initialState: TState = {
  customPenalty: '',
  customPenaltyReason: '',
};

const TutorPenaltyTab: FunctionComponent<TutorPenaltyTabProps> = ({
  lessonHistory,
  refreshListEnabled = false,
  refreshList,
}) => {
  const dispatch = useAppDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (lessonHistory.tutorPenalty) {
      updateStateAttributes({
        customPenalty: lessonHistory.tutorPenalty.toString(),
        customPenaltyReason: lessonHistory.tutorPenaltyReason || '',
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onPenaltyApply = async (item: TPenaltyItem) => {
    setIsLoading(true);

    await dispatch(
      adminUpdateLessonHistoryTutorPenalty({
        lessonHistoryId: lessonHistory._id,
        penalty: item.penalty,
        penaltyReason: item.penaltyReason,
      })
    );

    updateStateAttributes({
      customPenalty: item.penalty.toString(),
      customPenaltyReason: item.penaltyReason,
    });

    setIsLoading(false);

    if (refreshListEnabled && refreshList) {
      refreshList();
    }
  };

  const applyCustomPenalty = async () => {
    setIsLoading(true);

    if (
      state.customPenalty.trim().length &&
      state.customPenaltyReason.trim().length
    ) {
      await dispatch(
        adminUpdateLessonHistoryTutorPenalty({
          lessonHistoryId: lessonHistory._id,
          penalty: parseFloat(state.customPenalty),
          penaltyReason: state.customPenaltyReason.trim(),
        })
      );
    }

    setIsLoading(true);

    if (refreshListEnabled && refreshList) {
      refreshList();
    }
  };

  const onRemovePenalty = async () => {
    setIsLoading(true);

    await dispatch(
      adminUpdateLessonHistoryTutorPenalty({
        lessonHistoryId: lessonHistory._id,
        penalty: 0,
      })
    );

    updateStateAttributes({
      customPenalty: '',
      customPenaltyReason: '',
    });

    setIsLoading(false);

    if (refreshListEnabled && refreshList) {
      refreshList();
    }
  };

  return (
    <Fade in>
      <div>
        <Grid container spacing={2}>
          {penaltyData.map((item, index) => {
            const isApplied =
              item.penalty === lessonHistory.tutorPenalty &&
              item.penaltyReason === lessonHistory.tutorPenaltyReason;

            return (
              <Grid item xs={12} key={`penalty-li-item${index}`}>
                <Card elevation={0} sx={penaltyCardContainerStyles}>
                  <CardContent style={{ padding: '16px' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        fontWeight={500}
                        fontSize="16px"
                        color="#1E293B"
                      >
                        {item.title}
                      </Typography>
                      <ButtonBase
                        disabled={isLoading}
                        onClick={() => onPenaltyApply(item)}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          color: '#1E293B',
                          fontSize: '14px',
                          fontWeight: 600,
                          border: '1px solid #94A3B8',
                          borderRadius: '8px',
                          padding: '6px 12px',
                        }}
                      >
                        {isApplied ? 'Applied' : 'Apply'}
                      </ButtonBase>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          <Grid item xs={12}>
            <Typography fontSize="16px" fontWeight={600} color="#1E293B">
              Custom Penalty
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                  Custom Penalty (%)
                </Typography>
                <div style={{ minWidth: '130px', maxWidth: '130px' }}>
                  <TPTextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Penalty"
                    value={state.customPenalty}
                    InputProps={{ sx: { backgroundColor: '#fff' } }}
                    onChange={(event) =>
                      updateStateAttributes({
                        customPenalty: event.target.value,
                      })
                    }
                  />
                </div>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <Collapse in={state.customPenalty.trim().length > 0}>
              <CustomContainer>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    Custom Penalty Reason
                  </Typography>
                  <Stack direction="row" alignItems="center" gap="10px">
                    <div style={{ minWidth: '200px', maxWidth: '200px' }}>
                      <TPTextField
                        fullWidth
                        size="small"
                        placeholder="Reason"
                        value={state.customPenaltyReason}
                        InputProps={{ sx: { backgroundColor: '#fff' } }}
                        onChange={(event) =>
                          updateStateAttributes({
                            customPenaltyReason: event.target.value,
                          })
                        }
                      />
                    </div>
                    <IconButton
                      onClick={applyCustomPenalty}
                      disabled={
                        isLoading || state.customPenaltyReason.trim().length < 3
                      }
                    >
                      <CheckIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </CustomContainer>
            </Collapse>
          </Grid>
          {lessonHistory.tutorPenalty ? (
            <Grid item xs={12}>
              <CustomContainer>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                    Remove Penalty
                  </Typography>
                  <Button
                    disabled={isLoading}
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={onRemovePenalty}
                  >
                    Remove
                  </Button>
                </Stack>
              </CustomContainer>
            </Grid>
          ) : null}
        </Grid>
      </div>
    </Fade>
  );
};

export default TutorPenaltyTab;
